import React, { useState, useEffect, useMemo } from 'react';
import {
  TextField,
  Box,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Autocomplete,
} from '@mui/material';
import { CloudUpload, Image as ImageIcon, School } from '@mui/icons-material';
import bannerService from '../../../services/BannerService';
import postsService from '../../../services/PostsService';
import coursesService from '../../../services/CoursesService';
import { stripHtml } from '../../../utils/stingUtils';

const BRAND = '#4DABA4';

const courseOptionLabel = (option) =>
  stripHtml(option?.title) || `Curso ${option?.id ?? ''}`;

const courseOptionSx = {
  '& p': { m: 0, display: 'inline' },
  '& strong, & b': { fontWeight: 700 },
  '& em, & i': { fontStyle: 'italic' },
  lineHeight: 1.35,
};

const BannerForm = ({ initialData, onSuccess, onCancel }) => {
  const [text, setText] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isActive, setIsActive] = useState(true);
  const [orden, setOrden] = useState(0);
  const [postId, setPostId] = useState('');
  const [courseId, setCourseId] = useState('');
  const [posts, setPosts] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const isPostPublic = (post) => {
    const value = post?.isPublic ?? post?.IsPublic ?? post?.public ?? post?.Public;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value.toLowerCase() === 'true';
    if (typeof value === 'number') return value === 1;
    return false;
  };

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoadingPosts(true);
        const response = await postsService.getPosts();
        const data = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response?.items)
              ? response.items
              : [];
        setPosts(data.filter(isPostPublic));
      } catch (error) {
        console.error('Error al cargar publicaciones para el banner:', error);
      } finally {
        setLoadingPosts(false);
      }
    };

    const fetchCourses = async () => {
      try {
        setLoadingCourses(true);
        const data = await coursesService.getCourses().catch(() => []);
        setCourses(coursesService.normalizeCoursesList(data) || []);
      } catch (error) {
        console.error('Error al cargar cursos para el banner:', error);
      } finally {
        setLoadingCourses(false);
      }
    };

    fetchPosts();
    fetchCourses();
  }, []);

  useEffect(() => {
    if (initialData) {
      setText(initialData.text || '');
      setIsActive(initialData.isActive !== undefined ? initialData.isActive : true);
      const o = initialData.orden ?? initialData.Orden;
      setOrden(o !== undefined && o !== null && !Number.isNaN(Number(o)) ? Number(o) : 0);

      const linkedPostId =
        initialData.postId ??
        initialData.PostId ??
        initialData.postID ??
        initialData.publicationId ??
        initialData.PublicationId ??
        '';
      const normalizedLinkedPostId =
        linkedPostId !== null && linkedPostId !== undefined && linkedPostId !== ''
          ? String(linkedPostId)
          : '';
      setPostId(normalizedLinkedPostId);

      const linkedCourseId = initialData.courseId ?? initialData.CourseId ?? '';
      setCourseId(linkedCourseId ? String(linkedCourseId) : '');

      if (initialData.imagePath) {
        setImagePreview(initialData.imagePath);
      }
    } else {
      setOrden(0);
      setPostId('');
      setCourseId('');
    }
  }, [initialData]);

  const selectedCourse = useMemo(
    () => courses.find((c) => String(c.id) === String(courseId)) || null,
    [courses, courseId]
  );

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showToast('La imagen no debe superar los 5MB', 'error');
        return;
      }
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!text.trim()) {
      showToast('El texto es requerido', 'error');
      return;
    }

    if (!initialData && !image) {
      showToast('La imagen es requerida', 'error');
      return;
    }

    if (postId) {
      const selectedPost = posts.find((post) => String(post.id) === String(postId));
      if (!selectedPost && posts.length > 0) {
        showToast('Solo puedes vincular publicaciones públicas', 'error');
        return;
      }
    }

    try {
      setIsSubmitting(true);
      const usuarioConCedula = JSON.parse(localStorage.getItem('usuarioConCedula') || '{}');

      const formData = {
        text: text.trim(),
        image: image,
        uploadedBy: usuarioConCedula.id || usuarioConCedula.cedula || '',
        isActive: isActive,
        orden: Number.isFinite(Number(orden)) ? Number(orden) : 0,
        postId: postId ? Number(postId) : null,
        courseId: courseId ? String(courseId) : '',
      };

      if (initialData) {
        await bannerService.updateBanner(initialData.id, formData);
        showToast('Banner actualizado exitosamente', 'success');
      } else {
        await bannerService.createBanner(formData);
        showToast('Banner creado exitosamente', 'success');
      }

      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 1000);
      }
    } catch (error) {
      console.error('Error al guardar banner:', error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data ||
        'Error al guardar el banner';
      showToast(typeof message === 'string' ? message : 'Error al guardar el banner', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} className="p-4 space-y-6">
      <Typography variant="h6" className="mb-4 font-bold text-green-aqua">
        {initialData ? 'Editar Banner' : 'Nuevo Banner'}
      </Typography>

      <TextField
        fullWidth
        label="Texto del Banner"
        value={text}
        onChange={(e) => setText(e.target.value)}
        required
        multiline
        rows={3}
        placeholder="Ingresa el texto que aparecerá en el banner"
        className="mb-4"
      />

      <TextField
        fullWidth
        type="number"
        label="Orden"
        value={orden}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '') {
            setOrden(0);
            return;
          }
          const n = parseInt(v, 10);
          setOrden(Number.isNaN(n) ? 0 : Math.max(0, n));
        }}
        inputProps={{ min: 0, step: 1 }}
        helperText="Menor número = aparece antes en el carrusel de inicio"
        className="mb-4"
      />

      <Autocomplete
        options={courses}
        loading={loadingCourses}
        getOptionLabel={courseOptionLabel}
        value={selectedCourse}
        onChange={(_, value) => setCourseId(value?.id ? String(value.id) : '')}
        isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            key={option.id}
            sx={{ ...courseOptionSx, py: 1 }}
          >
            <Box
              component="span"
              sx={courseOptionSx}
              dangerouslySetInnerHTML={{
                __html: option.title || `Curso ${option.id}`,
              }}
            />
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Curso vinculado (opcional)"
            placeholder="Buscar curso para inscripción desde el banner"
            helperText="El usuario podrá inscribirse al curso al hacer clic en el banner"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <School sx={{ ml: 1, mr: 0.5, color: BRAND, fontSize: 20 }} />
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        )}
        className="mb-4"
      />

      <FormControl fullWidth className="mb-4">
        <InputLabel id="banner-post-select-label">Publicación vinculada (opcional)</InputLabel>
        <Select
          labelId="banner-post-select-label"
          value={postId}
          onChange={(e) => setPostId(e.target.value)}
          label="Publicación vinculada (opcional)"
          disabled={loadingPosts}
        >
          <MenuItem value="">
            <em>Sin publicación</em>
          </MenuItem>
          {posts.map((post) => (
            <MenuItem key={post.id} value={String(post.id)}>
              {post.title || `Publicación ${post.id}`}
            </MenuItem>
          ))}
        </Select>
        <Typography variant="caption" className="mt-1 text-gray-500">
          Opcional. Puedes vincular un curso, una publicación pública, o ambos.
        </Typography>
      </FormControl>

      <Box className="space-y-4">
        <Typography variant="subtitle2" className="font-semibold text-gray-700">
          Imagen del Banner
        </Typography>

        {imagePreview && (
          <Box className="mb-4 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-4">
            <img
              src={imagePreview}
              alt="Preview"
              className="mx-auto max-h-64 max-w-full rounded-lg shadow-md"
            />
          </Box>
        )}

        <Button
          variant="outlined"
          component="label"
          startIcon={<CloudUpload />}
          className="w-full border-green-aqua text-green-aqua hover:bg-green-aqua/10"
          sx={{
            borderWidth: '2px',
            textTransform: 'none',
            padding: '12px',
            '&:hover': {
              borderWidth: '2px',
              backgroundColor: 'rgba(77, 171, 164, 0.1)',
            },
          }}
        >
          {image ? 'Cambiar Imagen' : 'Seleccionar Imagen'}
          <input type="file" hidden accept="image/*" onChange={handleImageChange} />
        </Button>

        {!initialData && !image && (
          <Typography variant="caption" className="mt-2 block text-red-500">
            * La imagen es requerida para crear un nuevo banner
          </Typography>
        )}
      </Box>

      <FormControlLabel
        control={
          <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} color="primary" />
        }
        label={
          <Typography className="font-medium">
            Banner Activo
            <Typography variant="caption" className="block text-gray-500">
              {isActive ? 'El banner será visible en el carrusel' : 'El banner estará oculto'}
            </Typography>
          </Typography>
        }
      />

      <Box className="flex gap-4 pt-4">
        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          className="flex-1 background-green-aqua text-white hover:bg-green-600"
          sx={{
            textTransform: 'none',
            padding: '12px',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 20px rgba(77, 171, 164, 0.4)',
            },
          }}
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <ImageIcon />}
        >
          {isSubmitting ? 'Guardando...' : initialData ? 'Actualizar Banner' : 'Crear Banner'}
        </Button>

        {onCancel && (
          <Button
            type="button"
            variant="outlined"
            onClick={onCancel}
            disabled={isSubmitting}
            className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50"
            sx={{
              textTransform: 'none',
              padding: '12px',
            }}
          >
            Cancelar
          </Button>
        )}
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BannerForm;
